import React, { ReactElement, useEffect, useMemo } from 'react';
import { Grid } from '@material-ui/core';
import { FormDialog } from '../../../components/form/FormDialog';
import { useUpdateGameDetails } from '../../../api/games/useGames';
import { useForm } from 'react-hook-form';
import { UpdateGameDetailsRequest } from '../../../api/interface/apiModels';
import { TextInput } from '../../../components/form/inputs/TextInput';
import { DateTimeInput } from '../../../components/form/inputs/DateTimeInput';
import { CheckboxInput } from '../../../components/form/inputs/CheckboxInput';
import { GameDetails } from '../../../api/games/model';
import SettingsIcon from '@material-ui/icons/Settings';
import { DeleteOutline } from '@material-ui/icons';
import Button from '@material-ui/core/Button';

const defaultValues: Omit<UpdateGameDetailsRequest, 'gameId'> = {
  additionalComments: undefined,
  expectedStartTimestamp: new Date(),
  location: '',
  spectatorsAllowed: false,
};

type GameDateAndLocationDialogProps = {
  gameId: string;
  gameDetails?: GameDetails;
};

export const GameDateAndLocationDialog = ({ gameId, gameDetails }: GameDateAndLocationDialogProps): ReactElement => {
  const initialValues: UpdateGameDetailsRequest = useMemo(
    () => ({
      gameId: gameId,
      expectedStartTimestamp: gameDetails.expectedStartTimestamp ?? defaultValues.expectedStartTimestamp,
      location: gameDetails.location ?? defaultValues.location,
      spectatorsAllowed: gameDetails.spectatorsAllowed ?? gameDetails.spectatorsAllowed,
    }),
    [gameId, gameDetails]
  );
  const useFormReturn = useForm<UpdateGameDetailsRequest>();
  const mutation = useUpdateGameDetails();

  useEffect(() => {
    useFormReturn.reset({ ...initialValues });
  }, [initialValues, useFormReturn]);

  const remove = () => {
    mutation.mutate({
      gameId: gameId,
      location: null,
      expectedStartTimestamp: null,
      spectatorsAllowed: false,
      additionalComments: null,
    });
    useFormReturn.reset({ ...initialValues });
  };

  const deleteButton = (
    <Button variant="outlined" size="small" color="primary" onClick={remove}>
      <DeleteOutline />
    </Button>
  );

  return (
    <>
      <FormDialog
        dialogTitel={'Spieltermin & Ort'}
        useFormReturn={useFormReturn}
        mutation={mutation}
        ButtonComponent={<SettingsIcon style={{ color: 'grey', cursor: 'pointer' }} />}
        leftActionButton={deleteButton}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DateTimeInput name={'expectedStartTimestamp'} label={'Startzeitpunkt'} control={useFormReturn.control} />
          </Grid>
          <Grid item xs={12}>
            <TextInput name={'location'} label={'Spielort'} useFormReturn={useFormReturn} />
          </Grid>
          <Grid item xs={12}>
            <CheckboxInput name={'spectatorsAllowed'} useFormReturn={useFormReturn} label={'Gäste erwünscht?'} />
          </Grid>
        </Grid>
      </FormDialog>
    </>
  );
};
